import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import settings from "../../../constants/settings";
import useFlashPCBWebsocket from "../../websocket";
import { PartForIdMessage, PartsStore, SendRequest } from "./PartsStore";

export const PartsSubscriptionContext = createContext<PartsStore | null>(null);

function getServerSnapshotHasFollowers(): boolean {
  return false;
}

export default function PartsSubscriptionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [store] = useState(() => {
    return new PartsStore();
  });
  const hasFollowers = useSyncExternalStore(
    store.subscribe,
    store.hasFollowers,
    getServerSnapshotHasFollowers,
  );

  // Open the socket the first time anything on the page wants a part, then
  // keep it open: pages come and go, and re-handshaking on every navigation
  // would be more churn than one idle connection.
  const [socketOpened, setSocketOpened] = useState(false);
  useEffect(() => {
    if (hasFollowers) {
      setSocketOpened(true);
    }
  }, [hasFollowers]);

  const onMessage = useCallback(
    (message: PartForIdMessage) => {
      store.receive(message);
    },
    [store],
  );

  // sendJsonMessage is produced by the hook below but needed inside its
  // options, so route it through a ref.
  const sendRef = useRef<SendRequest>(() => {
    // Replaced once the websocket hook has produced sendJsonMessage.
  });
  const options = useMemo(() => {
    return {
      onAuthenticated: () => {
        store.connected((request) => {
          sendRef.current(request);
        });
      },
      onClose: () => {
        store.disconnected();
      },
    };
  }, [store]);

  const { sendJsonMessage } = useFlashPCBWebsocket<PartForIdMessage>(
    socketOpened ? `${settings.api_url}/parts/subscribe` : null,
    onMessage,
    undefined,
    options,
  );

  useEffect(() => {
    sendRef.current = sendJsonMessage;
  }, [sendJsonMessage]);

  return (
    <PartsSubscriptionContext.Provider value={store}>
      {children}
    </PartsSubscriptionContext.Provider>
  );
}
